import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { RecordEntry } from "@/components/common/record-entry"
import { IndividualDBSChecks } from "@/features/individuals/components/individual-dbs-checks"
import { RecordFields } from "@/components/common/record-fields"
import { useRecordForm } from "@/hooks/use-record-form"
import { useAddDBSCheck, useRemoveDBSCheck } from "@/queries/individual-record"

const formId = "individual-dbs-check"

const empty = {
  level: "basic",
  certificateNumber: "",
  issuedOn: "",
  outcome: "pending",
  disclosure: "",
  updateService: "no",
}

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/individuals/$individualId/checks/edit/"
)({
  component: EditChecksPage,
})

/**
 * A renewal is a new record rather than an edit: what was held in 2026 has to
 * still read as what was held in 2026.
 */
function EditChecksPage() {
  const { individualId } = Route.useParams()
  const navigate = useNavigate()
  const remove = useRemoveDBSCheck(individualId)

  const form = useRecordForm({
    initial: empty,
    mutation: useAddDBSCheck(individualId),
    toValues: () => empty,
    toInput: (values) => ({
      level: values.level,
      certificateNumber: values.certificateNumber || undefined,
      issuedOn: values.issuedOn || undefined,
      outcome: values.outcome || "pending",
      disclosure: values.disclosure || undefined,
      updateService: values.updateService === "yes",
    }),
  })

  return (
    <RecordEntry
      title="Record a check"
      heading="DBS check"
      description="A check that has been applied for has not come back: pending is not clear. The certificate is optional and can be added to the check once it is recorded."
      formId={formId}
      form={form}
      onSaved={() =>
        navigate({
          from: "/$organisation",
          to: "/$organisation/individuals/$individualId/checks",
          params: { individualId },
        })
      }
    >
      <IndividualDBSChecks
        individualId={individualId}
        onRemove={(id) => remove.mutate(id)}
      />

      <RecordFields
        form={form}
        fields={[
          {
            name: "level",
            label: "Level",
            options: [
              { value: "basic", label: "Basic" },
              { value: "standard", label: "Standard" },
              { value: "enhanced", label: "Enhanced" },
            ],
          },
          {
            name: "outcome",
            label: "Outcome",
            options: [
              { value: "pending", label: "Pending" },
              { value: "clear", label: "Clear" },
              { value: "disclosure", label: "Disclosure" },
            ],
          },
          { name: "certificateNumber", label: "Certificate number" },
          { name: "issuedOn", label: "Issued", type: "date" },
          {
            name: "disclosure",
            label: "What was disclosed",
            description:
              "Read by a human deciding fitness, never by a rule. Required where the outcome is a disclosure.",
          },
          {
            name: "updateService",
            label: "On the DBS update service",
            options: [
              { value: "no", label: "No" },
              { value: "yes", label: "Yes" },
            ],
          },
        ]}
      />
    </RecordEntry>
  )
}
